import React from 'react';

import { idsIncluded, joinIds } from '@/clientOnlyUtils/clientUtils';
import type { Stage } from '@poseidon/components/ContactCardBodyPure';
import type CadenceState from '@poseidon/types/pg-gen/cadenceState';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import { SmallPaddedScrollable } from '@poseidon/components/Scrollable';

import { LoadingContacts } from '@poseidon/components/LoadingContactCard';
import { contactOldToNewFormat, openContactDetailQuery, cadenceOldToNewFormat } from './ContactCard/ContactCard';
import type { ContactCardUser } from './ContactCard/ContactCardPure';
import { ContactCardPure } from './ContactCard/ContactCardPure';
import { ColumnsGrid } from '@poseidon/components/Grid';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { useContactSetStageMutation } from '~/hooks/trpc/useContactMutation';

type LocalContactField = { data?: string | null } | null;

type LocalContact = {
  id: string;
  primaryPictureUrl?: string | null;
  liOpenLink?: boolean | null;
  displayHonorific?: boolean;
  stageId?: string | null;
  signalsComputed?: boolean;
  triggeredSignals?: { bucketKey: string; bucketName: string; reason: string | null }[] | null;
  primaryFields: {
    firstName?: LocalContactField;
    lastName?: LocalContactField;
    phone?: { data?: string | null; verified?: boolean | null } | null;
    professionalEmail?: { data?: string | null; verified?: boolean | null } | null;
    title?: LocalContactField;
    honorificTitle?: LocalContactField;
  };
};

type LocalCadence = {
  id: string;
  isActive: boolean;
  name: string;
  state: CadenceState;
  running: boolean;
  nextStepTypes?: CadenceStepType[] | null;
  interruptMessage: string | null;
};

interface OrgContactItem {
  contact: LocalContact;
  activeCadence: {
    cadence: LocalCadence;
    cadenceUser: ContactCardUser;
  } | null;
  authedUser: ContactCardUser;
}
type OrgContactsData = OrgContactItem[];

interface InteractSectionProps {
  contacts: OrgContactsData | undefined;
  contactsLoading: boolean;
  dontClearOrg?: boolean;
  stages: Stage[] | undefined;
  stagesLoading: boolean;
}

export const OrgInteractSection = ({
  contacts,
  contactsLoading,
  dontClearOrg,
  stages,
  stagesLoading,
}: InteractSectionProps) => {
  const contactSetStageMutation = useContactSetStageMutation();
  const [query, setQuery] = useRouterQuery();
  return (
    <SmallPaddedScrollable>
      <ColumnsGrid gap={6} columns={1} columnMinWidth={0}>
        {contactsLoading && <LoadingContacts />}
        {contacts?.map((data) => (
          <ContactCardPure
            contact={contactOldToNewFormat(data.contact)}
            contactUser={data.activeCadence?.cadenceUser ?? data.authedUser}
            key={data.contact.id}
            cadence={cadenceOldToNewFormat(data.activeCadence?.cadence, data.activeCadence?.cadenceUser ?? data.authedUser)}
            cadenceSteps={null}
            org={null}
            canNotRunStepTypes={undefined}
            stages={stages ?? []}
            stagesLoading={stagesLoading}
            stageUpdating={contactSetStageMutation.isPending && contactSetStageMutation.variables?.contactId === data.contact.id}
            onStageChange={(stageId) => contactSetStageMutation.mutate({ contactId: data.contact.id, stageId })}
            onClick={() =>
              setQuery((q) =>
                openContactDetailQuery(q, {
                  contactId: data.contact.id,
                  cadenceId: data.activeCadence?.cadence.id,
                  dontClearOrg,
                }),
              )
            }
            isActive={query.id === joinIds({ contactId: data.contact.id, cadenceId: data.activeCadence?.cadence.id })}
            isChecked={idsIncluded({ contactId: data.contact.id, cadenceId: data.activeCadence?.cadence.id }, query.selectedIds)}
          />
        ))}
      </ColumnsGrid>
    </SmallPaddedScrollable>
  );
};
